import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import {
  type OtherExpenseCustomItem,
  type OtherExpenseFormType,
  getOtherExpenseFormSchema,
} from "Forms/Schemas/Budget/Expenses/otherExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetOtherExpenseQueryKey,
  useGetOtherExpense,
  useReplaceOtherExpense,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type OtherAmount, OtherExpenseEntryMode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/AddExpenseDrawer";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

const emptyCustomItem: OtherExpenseCustomItem = {
  name: "",
  value: "",
};

export const OtherExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<boolean>(false);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: otherExpenseData } = useGetOtherExpense(analysisId, versionId);
  const { mutateAsync: replaceOtherExpenseAsync } = useReplaceOtherExpense();

  const { control, setValue, watch, handleSubmit, clearErrors } = useForm<OtherExpenseFormType>({
    resolver: yupResolver(getOtherExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: (otherExpenseData?.aggregatedAmount?.value.toString() as any) || "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");

  useEffect(() => {
    if (detailed && customItems) {
      const total = customItems.reduce((sum, item) => {
        const value = Number.parseFloat(item.value ?? "0") || 0;
        return sum + value;
      }, 0);
      setValue("aggregatedAmount", total.toString());
    }
  }, [customItems, detailed, setValue]);

  // Function to recalculate total on every input change
  const recalculateTotal = () => {
    if (detailed && customItems) {
      const total = customItems.reduce((sum, item) => {
        const value = Number.parseFloat(item.value ?? "0") || 0;
        return sum + value;
      }, 0);
      setValue("aggregatedAmount", total.toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      setOpenDeleteBulkModal(true);
    },
  }));

  useEffect(() => {
    if (otherExpenseData) {
      handleChangeDetailed(otherExpenseData.entryMode === OtherExpenseEntryMode.individual);
      setValue("aggregatedAmount", (otherExpenseData.aggregatedAmount?.value ?? 0).toString());

      const customItems: { name: string; value: string }[] = [];
      for (const expense of otherExpenseData.amounts) {
        customItems.push({
          name: expense.name,
          value: expense.amount.value.toString(),
        });
      }
      replace(customItems.length > 0 ? customItems : [{ ...emptyCustomItem }]);
    } else {
      replace([{ ...emptyCustomItem }]);
    }
  }, [otherExpenseData, setValue, replace]);

  const onSubmit = async (data: OtherExpenseFormType) => {
    if (Number(data.aggregatedAmount) === 0) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseAmountZeroError));
      return;
    }

    const customItems: OtherAmount[] =
      data.customItems?.map((field) => ({
        amount: {
          value: Number.parseFloat(field.value ?? "0") || 0,
          currencyCode: "CZK",
          frequency: AmountFrequency.monthly,
        },
        name: field.name ?? "",
      })) ?? [];

    const amounts: OtherAmount[] = detailed ? customItems : [];

    try {
      await replaceOtherExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? OtherExpenseEntryMode.individual : OtherExpenseEntryMode.aggregate,
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          amounts,
        },
      });

      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    } finally {
      queryClient.invalidateQueries({ queryKey: getGetOtherExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
    }
  };

  const onDelete = async () => {
    try {
      await replaceOtherExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? OtherExpenseEntryMode.individual : OtherExpenseEntryMode.aggregate,
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
          amounts: [],
        },
      });

      queryClient.invalidateQueries({ queryKey: getGetOtherExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = () => {
    replace([]);
    setOpenDeleteBulkModal(false);
    onDelete();
  };

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    setValue("detailed", value);
    clearErrors();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.Other)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <FormNumberField
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                valueType="text"
                scale={2}
                min={0}
                thousandsSeparator=" "
                unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                disabled={detailed || readOnly}
                placeholder="0"
                fullWidth
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox checked={detailed} disabled={readOnly} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                {customFields.map((field, idx) => (
                  <React.Fragment key={field.id}>
                    <Grid size={6}>
                      <FormTextField
                        control={control}
                        name={`customItems.${idx}.name`}
                        label={idx === 0 ? t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item) : undefined}
                        disabled={readOnly}
                        placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={5}>
                      <FormNumberField
                        control={control}
                        name={`customItems.${idx}.value`}
                        label={idx === 0 ? t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Value) : undefined}
                        valueType="text"
                        scale={2}
                        min={0}
                        thousandsSeparator=" "
                        unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                        disabled={readOnly}
                        placeholder="0"
                        onChange={recalculateTotal}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={1} display="flex" alignItems="flex-start" mt={5} justifyContent="center">
                      <Button disabled={readOnly} variant="text" color="error" onClick={() => remove(idx)}>
                        <DeleteIcon />
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid size={12}>
                  <Button variant="text" disabled={readOnly} startIcon={<AddIcon />} onClick={() => append({ name: "", value: "" })}>
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>

      <DialogModal
        open={openDeleteBulkModal}
        onClose={() => setOpenDeleteBulkModal(false)}
        closeLabel={t(Resources.Common.Close)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
        subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint)}
        primaryAction={
          <Button variant="contained" disabled={readOnly} onClick={onDeleteBulkExpense}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </form>
  );
});
